import React from "react";
import { useIntl } from "react-intl";
import { Popup, Table } from "semantic-ui-react";
import LegendPill from "./LegendPill";
import LegendBar from "./LegendBar";
import { getColorIndicator } from "../../../libs/common_utils";
import { isUndefined } from "lodash";

const DailyPacingPopup = ({ data, nullishCurrencyFormat }) => {
  const intl = useIntl();
  const roundedPacingAtPercentage = data.campaign_daily_pacing_at
    ? Math.abs(Math.round(data.campaign_daily_pacing_at))
    : 0;
  const underspend = data?.campaign_daily_pacing_under_spend ?? 0;
  // If underspend is positive, it's underspend. If underspend is negative, it's overspend.
  const isUnderspend = underspend > 0;

  return isUndefined(data.campaign_daily_pacing_at) ? (
    <Table.Cell>-</Table.Cell>
  ) : (
    <Table.Cell key={`campaign-daily-pacing-${data.id}`}>
      <Popup
        on="hover"
        hoverable
        hideOnScroll={false}
        position="top center"
        wide
        trigger={
          <span>
            {nullishCurrencyFormat(data.total_spend_yesterday)} /{" "}
            {nullishCurrencyFormat(data.campaign_daily_pacing_spend_to_pace)}
          </span>
        }
        content={
          <div
            style={{
              minWidth: 520,
              padding: 16,
              display: "grid",
              gridTemplateRows: "auto 1px auto",
              rowGap: 12,
            }}
          >
            {/* Header row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                columnGap: 24,
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {intl.formatMessage({
                  id: "LABEL_PACING_AT",
                  defaultMessage: "Pacing at",
                })}{" "}
                {`${roundedPacingAtPercentage}%`}
              </div>

              <div
                style={{ fontSize: 16, fontWeight: 600, textAlign: "right" }}
              >
                {intl.formatMessage({
                  id: "LABEL_SPEND_TO_PACE",
                  defaultMessage: "Spend to Pace at",
                })}{" "}
                {nullishCurrencyFormat(
                  data.campaign_daily_pacing_spend_to_pace
                )}
              </div>
            </div>

            {/* divider */}
            <div style={{ height: 1, background: "#eee", borderRadius: 1 }} />

            {/* legend / values */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                columnGap: 24,
              }}
            >
              {/* Spend Yesterday */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: "#555",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  {/* green bar icon */}
                  <LegendBar
                    color={getColorIndicator(roundedPacingAtPercentage)}
                  />
                  {intl.formatMessage({
                    id: "LABEL_SPEND_YESTERDAY",
                    defaultMessage: "Spend Yesterday",
                  })}
                </div>
                <div style={{ fontSize: 16 }}>
                  {nullishCurrencyFormat(data.total_spend_yesterday)}
                </div>
              </div>

              {/* Underspend */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: "#555",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 4,
                  }}
                >
                  <LegendPill />
                  <span>
                    {isUnderspend
                      ? intl.formatMessage({
                          id: "LABEL_UNDERSPEND",
                          defaultMessage: "Underspend",
                        })
                      : intl.formatMessage({
                          id: "LABEL_OVERSPEND",
                          defaultMessage: "Overspend",
                        })}
                  </span>
                </div>
                <div style={{ fontSize: 16 }}>
                  {nullishCurrencyFormat(Math.abs(underspend))}
                </div>
              </div>

              {/* Daily Spend Cap */}
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: "#555",
                    textTransform: "uppercase",
                    marginBottom: 4,
                  }}
                >
                  {intl.formatMessage({
                    id: "LABEL_DAILY_SPEND_CAP",
                    defaultMessage: "Daily Spend Cap",
                  })}
                </div>
                <div style={{ fontSize: 16 }}>
                  {nullishCurrencyFormat(
                    data.daily_spend_cap ??
                      data.campaign_daily_pacing_spend_to_pace
                  )}
                  {data.daily_spend_cap_is_auto ? " (Auto)" : ""}
                </div>
              </div>
            </div>
          </div>
        }
      />
    </Table.Cell>
  );
};

export default DailyPacingPopup;
